import { getDocument } from '@atlaskit/browser-apis';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorState, ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';

export type InlineSuggestionConfig = {
	intervalMs: number;
	prefix: string;
	suggestions: string[];
};

type InlineSuggestionPluginState = {
	currentIndex: number;
	dismissed: boolean;
	hasShown: boolean;
};

type InlineSuggestionPluginMeta = {
	currentIndex?: number;
};

const INLINE_SUGGESTION_CLASS_NAME =
	'placeholder-decoration placeholder-decoration-inline creation-inline-suggestion';
const INLINE_SUGGESTION_TEST_ID = 'creation-inline-suggestion';
const inlineSuggestionPluginKey = new PluginKey<InlineSuggestionPluginState>(
	'creationInlineSuggestionPlugin',
);

const getInlineSuggestionPosition = (
	editorState: EditorState,
	prefix: string,
): number | undefined => {
	const { doc, selection } = editorState;
	const paragraph = doc.firstChild;

	if (
		doc.childCount !== 1 ||
		paragraph?.type.name !== 'paragraph' ||
		doc.textContent !== prefix ||
		!selection.empty
	) {
		return undefined;
	}

	const endOfParagraph = doc.content.size - 1;
	return selection.from === endOfParagraph ? endOfParagraph : undefined;
};

const createInlineSuggestionDecoration = ({
	currentIndex,
	editorState,
	suggestions,
	position,
}: {
	currentIndex: number;
	editorState: EditorState;
	position: number;
	suggestions: string[];
}): DecorationSet => {
	const suggestion = suggestions[currentIndex % suggestions.length];

	return DecorationSet.create(editorState.doc, [
		Decoration.widget(
			position,
			() => {
				const doc = getDocument();
				if (!doc) {
					throw new Error('Document not found');
				}

				const suggestionElement = doc.createElement('span');
				suggestionElement.className = INLINE_SUGGESTION_CLASS_NAME;
				suggestionElement.setAttribute('aria-hidden', 'true');
				suggestionElement.setAttribute('contenteditable', 'false');
				suggestionElement.setAttribute('data-testid', INLINE_SUGGESTION_TEST_ID);
				suggestionElement.textContent = suggestion;
				return suggestionElement;
			},
			{
				key: `creation-inline-suggestion-${currentIndex}`,
				side: 1,
			},
		),
	]);
};

const prefersReducedMotion = (): boolean =>
	typeof globalThis.matchMedia === 'function' &&
	globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const creationInlineSuggestionPlugin: NextEditorPlugin<
	'creationInlineSuggestion',
	{
		pluginConfiguration: InlineSuggestionConfig | undefined;
	}
> = ({ config }) => ({
	name: 'creationInlineSuggestion',
	pmPlugins() {
		if (!config || config.suggestions.length === 0) {
			return [];
		}

		return [
			{
				name: 'creationInlineSuggestion',
				plugin: () =>
					new SafePlugin<InlineSuggestionPluginState>({
						key: inlineSuggestionPluginKey,
						state: {
							init: (_config: unknown, editorState: EditorState) => {
								const hasShown =
									getInlineSuggestionPosition(editorState, config.prefix) !== undefined;
								return { currentIndex: 0, dismissed: false, hasShown };
							},
							apply: (
								transaction: ReadonlyTransaction,
								pluginState: InlineSuggestionPluginState,
								_oldEditorState: EditorState,
								newEditorState: EditorState,
							) => {
								const meta = transaction.getMeta(inlineSuggestionPluginKey) as
									| InlineSuggestionPluginMeta
									| undefined;
								const isEligible =
									getInlineSuggestionPosition(newEditorState, config.prefix) !== undefined;
								const hasShown = pluginState.hasShown || isEligible;
								const dismissed =
									pluginState.dismissed ||
									(pluginState.hasShown &&
										!isEligible &&
										(transaction.docChanged || transaction.selectionSet));

								return {
									currentIndex: meta?.currentIndex ?? pluginState.currentIndex,
									dismissed,
									hasShown,
								};
							},
						},
						props: {
							decorations(editorState: EditorState) {
								const pluginState = inlineSuggestionPluginKey.getState(editorState);
								const position = getInlineSuggestionPosition(editorState, config.prefix);

								if (!pluginState || pluginState.dismissed || position === undefined) {
									return undefined;
								}

								return createInlineSuggestionDecoration({
									currentIndex: pluginState.currentIndex,
									editorState,
									position,
									suggestions: config.suggestions,
								});
							},
							handleDOMEvents: {
								mousedown(view: EditorView) {
									const pluginState = inlineSuggestionPluginKey.getState(view.state);
									const position = getInlineSuggestionPosition(view.state, config.prefix);

									if (!pluginState || pluginState.dismissed || position === undefined) {
										return false;
									}

									const suggestion =
										config.suggestions[pluginState.currentIndex % config.suggestions.length];
									view.dispatch(view.state.tr.insertText(suggestion, position));
									return false;
								},
							},
						},
						view(editorView: EditorView) {
							let intervalId: ReturnType<typeof setInterval> | undefined;
							let timeoutId: ReturnType<typeof setTimeout> | undefined;
							let hasReducedMotionAdvanced = false;

							const clearTimers = () => {
								if (intervalId !== undefined) {
									clearInterval(intervalId);
									intervalId = undefined;
								}
								if (timeoutId !== undefined) {
									clearTimeout(timeoutId);
									timeoutId = undefined;
								}
							};

							const advanceSuggestion = (view: EditorView, index?: number) => {
								const pluginState = inlineSuggestionPluginKey.getState(view.state);
								if (!pluginState || pluginState.dismissed) {
									return;
								}

								const currentIndex =
									index ?? (pluginState.currentIndex + 1) % config.suggestions.length;
								view.dispatch(view.state.tr.setMeta(inlineSuggestionPluginKey, { currentIndex }));
							};

							const updateTimer = (view: EditorView) => {
								const pluginState = inlineSuggestionPluginKey.getState(view.state);
								const isVisible =
									pluginState !== undefined &&
									!pluginState.dismissed &&
									getInlineSuggestionPosition(view.state, config.prefix) !== undefined;

								if (!isVisible || config.suggestions.length < 2) {
									clearTimers();
									return;
								}

								if (prefersReducedMotion()) {
									if (timeoutId === undefined && !hasReducedMotionAdvanced) {
										timeoutId = setTimeout(() => {
											timeoutId = undefined;
											hasReducedMotionAdvanced = true;
											advanceSuggestion(view, 1);
										}, config.intervalMs);
									}
									return;
								}

								if (intervalId === undefined) {
									intervalId = setInterval(() => {
										advanceSuggestion(view);
									}, config.intervalMs);
								}
							};

							updateTimer(editorView);

							return {
								update: updateTimer,
								destroy: clearTimers,
							};
						},
					}),
			},
		];
	},
});
